import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { ContentTemplate, Progress, Typography } from 'web-library';
import { candidateService } from '../../../api';
import { useResultNavigation } from '../../../routes/ResultRoute';
import { parseLocationSearch } from '../../../utils/StringUtils';
import IndividualResultRelease from '../IndividualResultRelease';
import { useAuditViewResultRequestMutation } from '../../../api/candidate/result/@candidate/queries-services/queries';
import { useTriggerMutate } from '../../../hooks/query';
import { useAppealEligibilityQuery } from '../../../api/candidate/appeal/@candidate/queries-services/queries';
import { usePostCheckEligibility } from '../../../api-refactored/resit-registration/usePostCheckEligibility';

const ResultDetail = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const { series: examSeries, year: examYear, code: examCode } = parseLocationSearch(
    location.search
  );

  const history = useHistory();
  const { toBlankPage } = useResultNavigation();
  const [isResitEligible, setIsResitEligible] = useState(false);
  const [isAppealEligible, setIsAppealEligible] = useState(false);

  const { data, isLoading, error } = candidateService.result.candidate.useExamResultsDetailsQuery(
    examSeries || '',
    examYear || '',
    examCode || '',
    (err) => {
      if (err.response?.data.code === '1008' && err.response?.status === 423) {
        history.push(toBlankPage);
      }
    }
  );

  const viewResult = useAuditViewResultRequestMutation();

  const viewResultMutation = useCallback(() => {
    viewResult.mutate();
  }, [viewResult]);

  const setTriggerResultMutate = useTriggerMutate(viewResultMutation);

  useEffect(() => {
    setTriggerResultMutate((prev) => prev + 1);
  }, [setTriggerResultMutate]);

  /** to check eligibility to resit MT
   * */
  const {
    mutate: postCheckEligibility,
    isLoading: isResitEligibilityLoading,
  } = usePostCheckEligibility({
    onSuccess: (response) => {
      setIsResitEligible(response.eligible);
    },
    onError: () => {
      setIsResitEligible(false);
    },
  });

  const checkEligibilityMutation = useCallback(() => {
    postCheckEligibility({ accessLink: false });
  }, [postCheckEligibility]);

  const setTriggerCheckEligibilityMutate = useTriggerMutate(checkEligibilityMutation);

  useEffect(() => {
    setTriggerCheckEligibilityMutate((prev) => prev + 1);
  }, [setTriggerCheckEligibilityMutate]);

  /**
   * to check eligibility to appeal
   */
  const { isLoading: isAppealEligibilityLoading } = useAppealEligibilityQuery(
    // should never use unique link by pass
    { q: examCode as string },
    {
      onSuccess: (response) => {
        setIsAppealEligible(response.data?.eligible);
      },
      onError: (err) => {
        setIsResitEligible(false);
      },
    }
  );

  if (isLoading || isResitEligibilityLoading || isAppealEligibilityLoading) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  if (error || !data) {
    return (
      <ContentTemplate>
        <Typography>{t('exam->>ResultDetail.Unexpected error encountered')}</Typography>
      </ContentTemplate>
    );
  }

  return (
    <IndividualResultRelease
      candidateResultData={data}
      eligibleForResit={isResitEligible}
      eligibleForAppeal={isAppealEligible}
    />
  );
};

export default ResultDetail;
